import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchAllDoubles, type RankingItem } from "../utils/homeApi";

type SortKey = "rating" | "gamesPlayed" | "gamesWon" | "tournamentsPlayed";

type RankedItem = RankingItem & { position: number };

const TeamPlayerRankings: React.FC = () => {
  const [teamPlayers, setTeamPlayers] = useState<RankingItem[]>([]);
  const [filteredTeamPlayers, setFilteredTeamPlayers] = useState<RankedItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [sortBy, setSortBy] = useState<SortKey>("rating");
  const itemsPerPage = 20;

  useEffect(() => {
    const fetchTeamPlayers = async () => {
      try {
        setLoading(true);
        const res = await fetchAllDoubles({ limit: 500 });
        let acc: RankingItem[] = res.items;
        let offset = res.items.length;
        while (offset < res.total) {
          const next = await fetchAllDoubles({ limit: 500, offset });
          acc = acc.concat(next.items);
          offset += next.items.length;
          if (next.items.length === 0) {
            break;
          }
        }
        setTeamPlayers(acc);
      } catch (error) {
        console.error("Error loading team player ratings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamPlayers();
  }, []);

  useEffect(() => {
    const sorted = [...teamPlayers].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
    // Position comes from the full list so searching never renumbers players.
    const ranked = sorted.map((player, index) => ({ ...player, position: index + 1 }));
    const normalizedSearch = searchTerm.toLowerCase();
    const filtered = ranked.filter((player) =>
      player.name?.toLowerCase().includes(normalizedSearch),
    );

    setFilteredTeamPlayers(filtered);
    setCurrentPage(1);
  }, [teamPlayers, searchTerm, sortBy, sortOrder]);

  const getRatingBadgeClass = (rating: number): string => {
    if (rating >= 2500) {
      return "very-high";
    }

    if (rating >= 2200) {
      return "high";
    }

    if (rating >= 1900) {
      return "medium";
    }

    if (rating >= 1600) {
      return "low";
    }

    return "very-low";
  };

  const totalPages = Math.ceil(filteredTeamPlayers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentTeamPlayers = filteredTeamPlayers.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  if (loading) {
    return (
      <div className="card">
        <h2>Wczytywanie…</h2>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <h2>Ranking deblowy</h2>
        <p>Liczba zawodników: {filteredTeamPlayers.length}</p>
      </div>

      <div className="search-container">
        <input
          type="text"
          name="player-filter"
          autoComplete="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          placeholder="Szukaj zawodników…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="card">
        <div className="sort-controls">
          <label className="sort-controls__field">
            <span className="sort-controls__label">Sortuj według</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortKey)}
              className="sort-controls__select"
            >
              <option value="rating">Rating</option>
              <option value="gamesPlayed">Rozegrane mecze</option>
              <option value="gamesWon">Wygrane</option>
              <option value="tournamentsPlayed">Turnieje</option>
            </select>
          </label>
          <button
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            className="btn-primary"
          >
            {sortOrder === "asc" ? "↑ Rosnąco" : "↓ Malejąco"}
          </button>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Miejsce</th>
                <th>Zawodnik</th>
                <th>Rating</th>
                <th>Rozegrane mecze</th>
                <th>Wygrane</th>
                <th>Przegrane</th>
                <th>Remisy</th>
                <th title="Procent wygranych spotkań. Najedź na wartość żeby zobaczyć remisy/przegrane.">% Wygranych</th>
                <th title="Zmiana ratingu w ostatnim turnieju">Δ ostatni turniej</th>
                <th>Turnieje</th>
              </tr>
            </thead>
            <tbody>
              {currentTeamPlayers.map((player, index) => (
                <tr key={player.playerId ?? `player-${startIndex + index}`}>
                  <td>{player.position}</td>
                  <td>
                    <Link
                      to={`/player/${player.playerId}?tab=debel`}
                      className="player-link"
                    >
                      {player.name || "Nieznany zawodnik"}
                    </Link>
                  </td>
                  <td>
                    <span
                      className={`rating-badge ${getRatingBadgeClass(player.rating)}`}
                    >
                      {player.rating}
                    </span>
                  </td>
                  <td>{player.gamesPlayed}</td>
                  <td>{player.gamesWon}</td>
                  <td>{player.gamesLost}</td>
                  <td>{player.gamesDrawn}</td>
                  <td title={player.gamesPlayed > 0
                    ? `Wygrane ${((player.gamesWon / player.gamesPlayed) * 100).toFixed(1)}% · Remisy ${((player.gamesDrawn / player.gamesPlayed) * 100).toFixed(1)}% · Przegrane ${((player.gamesLost / player.gamesPlayed) * 100).toFixed(1)}%`
                    : 'Brak meczów'}>
                    {player.gamesPlayed > 0
                      ? `${((player.gamesWon / player.gamesPlayed) * 100).toFixed(1)}%`
                      : '—'}
                  </td>
                  <td
                    style={{
                      color: player.ratingDifference >= 0 ? "green" : "red",
                    }}
                  >
                    {player.ratingDifference >= 0 ? "+" : ""}
                    {player.ratingDifference}
                  </td>
                  <td>{player.tournamentsPlayed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="btn-secondary"
              style={{ marginRight: "1rem" }}
            >
              Poprzednia
            </button>
            <span style={{ margin: "0 1rem" }}>
              Strona {currentPage} z {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="btn-secondary"
              style={{ marginLeft: "1rem" }}
            >
              Następna
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamPlayerRankings;
